// hooks
import { useEffect, useState } from "react";
import { useSignup } from "hooks/useSignup";

// react-router-dom components
import { Link } from "react-router-dom";
import {
  CircularProgress,
  IconButton,
  MenuItem,
  InputAdornment,
} from "@mui/material";

// @mui material components
import Card from "@mui/material/Card";
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { Visibility, VisibilityOff } from "@mui/icons-material";

// Images
import facultyimage from "assets/images/faculty-building.jpg";
import BasicLayout from "../components/BasicLayout";
import { API_URL } from "../../../config";
import { toast } from "sonner";

function Cover() {
  const [selectedProgramme, setSelectedProgramme] = useState(null);
  const [programmes, setProgrammes] = useState([]);
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [regNumber, setRegNumber] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [yearOfStudy, setYearOfStudy] = useState("");
  const [academicyear, setAcademicYear] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countryCode, setCountryCode] = useState("+256");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { signup, error, isLoading } = useSignup();

  useEffect(() => {
    if (error) {
      toast.error("Error", { description: error });
    }
  }, [error]);

  //Fetch programmes from the server
  useEffect(() => {
    const fetchProgrammes = async () => {
      try {
        const response = await fetch(`${API_URL}/programmes/getprogrammes`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            // Authorization: `Bearer ${user.token}`,
          },
        });

        const programmesjson = await response.json();

        if (response.ok) {
          setProgrammes(programmesjson);
        }
        if (!response.ok) {
          toast.error("Error", { description: "Failed to fetch data" });
        }
      } catch (error) {
        console.log("Error fetching data:", { description: error });
      }
    };

    fetchProgrammes();
  }, []);
  // Programmes from Backend ends here

  //Fetch academicyears from the server
  useEffect(() => {
    const fetchAcademicYears = async () => {
      try {
        const response = await fetch(`${API_URL}/academicyears/current`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            // Authorization: `Bearer ${user.token}`,
          },
        });

        const academicyearsjson = await response.json();

        if (response.ok) {
          setAcademicYear(academicyearsjson.year);
        }
        if (!response.ok) {
          toast.error("Failed to fetch data");
        }
      } catch (error) {
        toast.error("Error fetching data:", { description: error });
      }
    };

    fetchAcademicYears();
  }, []);
  // Academic Years from Backend ends here

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Remove leading 0 from phone number if it exists
    let formattedPhoneNumber = phoneNumber;
    if (phoneNumber.startsWith("0")) {
      formattedPhoneNumber = phoneNumber.substring(1);
    }
    formattedPhoneNumber = countryCode + formattedPhoneNumber;

    const selectedProgrammeCode = selectedProgramme
      ? programmes.find(
          (programme) => programme.programmename === selectedProgramme
        ).programmecode
      : "";

    await signup(
      firstname,
      lastname,
      email,
      selectedProgrammeCode,
      academicyear,
      password,
      confirmPassword,
      regNumber,
      formattedPhoneNumber,
      yearOfStudy
    );
    setIsSubmitting(false);
  };

  // Generate years of study based on selected programme duration
  const generateYearsOfStudy = (duration) => {
    return Array.from({ length: duration }, (_, i) => duration - i);
  };

  return (
    <BasicLayout image={facultyimage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="success"
          mx={2}
          mt={-3}
          p={3}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Start your Research Journey Today
          </MDTypography>
          <MDTypography display="block" variant="button" color="white" my={1}>
            Register Here
          </MDTypography>
        </MDBox>
        <MDBox pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            <MDBox mb={2} display="flex" justifyContent="space-between">
              <MDBox mr={1} flex={1}>
                <MDInput
                  type="text"
                  label="First Name"
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value.toUpperCase())}
                  variant="standard"
                  fullWidth
                />
              </MDBox>
              <MDBox ml={1} flex={1}>
                <MDInput
                  type="text"
                  label="Last Name"
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value.toUpperCase())}
                  variant="standard"
                  fullWidth
                />
              </MDBox>
            </MDBox>
            <MDBox mb={2} display="flex" justifyContent="space-between">
              <MDBox mr={1} flex={1}>
                <MDInput
                  type="email"
                  label="Student Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.toLowerCase())}
                  variant="standard"
                  fullWidth
                />
              </MDBox>
              <MDBox ml={1} flex={1}>
                <MDInput
                  type="text"
                  label="Registration Number"
                  value={regNumber}
                  onChange={(e) => setRegNumber(e.target.value.toUpperCase())}
                  variant="standard"
                  fullWidth
                />
              </MDBox>
            </MDBox>
            <MDBox mb={2} display="flex" justifyContent="space-between">
              {/* Password Field */}
              <MDBox mr={1} flex={1}>
                <MDInput
                  type={showPassword ? "text" : "password"}
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  variant="standard"
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </MDBox>

              {/* Confirm Password Field */}
              <MDBox ml={1} flex={1}>
                <MDInput
                  type={showConfirmPassword ? "text" : "password"}
                  label="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  variant="standard"
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </MDBox>
            </MDBox>
            {/* Programme */}
            <MDBox mb={2}>
              <MDInput
                variant="standard"
                label="Select Programme"
                select
                value={selectedProgramme}
                onChange={(e) => setSelectedProgramme(e.target.value)}
                fullWidth
                mb={3}
                sx={{
                  width: "100%",
                  "& .MuiSelect-select": {
                    height: "55px", 
                  },
                }} 
                SelectProps={{
                  MenuProps: { PaperProps: { style: { maxHeight: 150 } } },
                }}
              >
                {programmes.map((programme) => (
                  <MenuItem key={programme.id} value={programme.programmename}>
                    {programme.programmename}
                  </MenuItem>
                ))}
              </MDInput>
            </MDBox>
            <MDBox mb={2} display="flex" justifyContent="space-between">
              {/* Academic Year */}
              <MDBox>
                <MDInput
                  variant="standard"
                  label="Academic Year"
                  value={academicyear}
                  InputProps={{
                    readOnly: true,
                  }}
                  fullWidth
                />
              </MDBox>

              {/* Year of Study */}
              <MDBox ml={1} flex={1} mt={1}>
                <MDInput
                  variant="standard"
                  label="Year of Study"
                  select
                  value={yearOfStudy}
                  onChange={(e) => setYearOfStudy(e.target.value)}
                  fullWidth
                  disabled={selectedProgramme ? false : true}
                >
                  {selectedProgramme &&
                    generateYearsOfStudy(
                      programmes.find(
                        (programme) =>
                          programme.programmename === selectedProgramme
                      )?.programmeduration || 0
                    ).map((year) => (
                      <MenuItem key={year} value={year}>
                        {`Year ${year}`}
                      </MenuItem>
                    ))}
                </MDInput>
              </MDBox>
            </MDBox>
            <MDBox mb={2} display="flex" flexDirection="row">
              <MDBox mr={1} flex={1}>
                <MDInput
                  select
                  label="Country Code"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  variant="standard"
                  fullWidth
                >
                  <MenuItem value="+256" selected>
                    Uganda (+256)
                  </MenuItem>
                  <MenuItem value="+254">Kenya (+254)</MenuItem>
                  <MenuItem value="+255">Tanzania (+255)</MenuItem>
                  <MenuItem value="+250">Rwanda (+250)</MenuItem>
                  <MenuItem value="+252">Somalia (+252)</MenuItem>
                  <MenuItem value="+211">South Sudan (+211)</MenuItem>
                  <MenuItem value="+251">Ethiopia (+251)</MenuItem>
                  <MenuItem value="+253">Djibouti (+253)</MenuItem>
                  <MenuItem value="+257">Burundi (+257)</MenuItem>

                  {/* Add more country codes as needed */}
                </MDInput>
              </MDBox>

              <MDBox ml={1} flex={2}>
                <MDInput
                  type="text"
                  label="Phone Number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  variant="standard"
                  fullWidth
                />
              </MDBox>
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton
                type="submit"
                variant="gradient"
                color="info"
                fullWidth
                disabled={isLoading}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                ) : (
                  "Register"
                )}
              </MDButton>
            </MDBox>
            <MDBox mt={3} textAlign="center">
              <MDTypography variant="button" color="text">
                Already have an account?{" "}
                <MDTypography
                  component={Link}
                  to="/signin"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  Sign In
                </MDTypography>
              </MDTypography>
            </MDBox>
            <MDBox mt={3} textAlign="center">
              <MDTypography variant="button" color="text">
                <MDTypography
                  component={Link}
                  to="/landingpage"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  Back
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Cover;
